import Phaser from 'phaser';
import {
  COLOR_CSS,
  COLORS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  SAFE_AREA,
} from '../config';

export interface SceneHeaderConfig {
  title: string;
  kicker?: string;
  accent?: number;
  y?: number;
}

/**
 * Builds the shared command-deck backdrop used by every non-gameplay scene.
 * The repeated grid and edge rails make transitions feel like one product.
 */
export function createCommandDeckBackground(
  scene: Phaser.Scene,
  accent: number = COLORS.player
): Phaser.GameObjects.Container {
  const container = scene.add.container(0, 0).setDepth(-20);
  const base = scene.add.rectangle(
    GAME_WIDTH / 2,
    GAME_HEIGHT / 2,
    GAME_WIDTH,
    GAME_HEIGHT,
    COLORS.bgMenu
  );
  container.add(base);

  const atmosphere = scene.add.graphics();
  atmosphere.fillStyle(0x0B2636, 0.52);
  atmosphere.fillCircle(GAME_WIDTH - 36, 96, 190);
  atmosphere.fillStyle(0x06101A, 0.88);
  atmosphere.fillCircle(-40, GAME_HEIGHT - 70, 230);

  atmosphere.lineStyle(1, COLORS.grid, 0.24);
  for (let x = 20; x < GAME_WIDTH; x += 42) {
    atmosphere.lineBetween(x, SAFE_AREA.top, x, GAME_HEIGHT - SAFE_AREA.bottom);
  }
  for (let y = SAFE_AREA.top + 18; y < GAME_HEIGHT; y += 42) {
    atmosphere.lineBetween(0, y, GAME_WIDTH, y);
  }

  atmosphere.lineStyle(2, accent, 0.25);
  atmosphere.lineBetween(12, SAFE_AREA.top + 6, 12, GAME_HEIGHT - SAFE_AREA.bottom - 8);
  atmosphere.lineBetween(GAME_WIDTH - 12, SAFE_AREA.top + 6, GAME_WIDTH - 12, GAME_HEIGHT - SAFE_AREA.bottom - 8);
  container.add(atmosphere);

  const scan = scene.add.rectangle(
    GAME_WIDTH / 2,
    SAFE_AREA.top + 4,
    GAME_WIDTH - 32,
    2,
    accent,
    0.5
  );
  container.add(scan);
  scene.tweens.add({
    targets: scan,
    y: GAME_HEIGHT - SAFE_AREA.bottom - 6,
    alpha: { from: 0.36, to: 0.04 },
    duration: 5200,
    repeat: -1,
    ease: 'Linear',
  });

  return container;
}

export function createSceneHeader(
  scene: Phaser.Scene,
  config: SceneHeaderConfig
): Phaser.GameObjects.Container {
  const y = config.y ?? SAFE_AREA.top + 42;
  const accent = config.accent ?? COLORS.player;
  const container = scene.add.container(GAME_WIDTH / 2, y);

  const bracket = scene.add.graphics();
  bracket.lineStyle(2, accent, 0.86);
  bracket.lineBetween(-150, -22, -150, 22);
  bracket.lineBetween(-150, -22, -132, -22);
  bracket.lineBetween(150, -22, 150, 22);
  bracket.lineBetween(132, -22, 150, -22);
  bracket.lineStyle(1, accent, 0.35);
  bracket.lineBetween(-128, 25, 128, 25);
  container.add(bracket);

  if (config.kicker) {
    container.add(
      scene.add.text(0, -25, config.kicker.toUpperCase(), {
        fontFamily: FONTS.numbers,
        fontSize: '11px',
        color: `#${accent.toString(16).padStart(6, '0')}`,
        letterSpacing: 2,
      }).setOrigin(0.5)
    );
  }

  container.add(
    scene.add.text(0, 2, config.title.toUpperCase(), {
      fontFamily: FONTS.display,
      fontSize: '28px',
      color: COLOR_CSS.uiText,
      fontStyle: 'bold',
      letterSpacing: 2,
    }).setOrigin(0.5)
  );

  return container;
}

export function drawChamferedPanel(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  fill: number,
  border: number,
  alpha: number = 1,
  cut: number = 10
): void {
  graphics.fillStyle(fill, alpha);
  graphics.lineStyle(2, border, Math.min(1, alpha + 0.08));
  graphics.beginPath();
  graphics.moveTo(x + cut, y);
  graphics.lineTo(x + width - cut, y);
  graphics.lineTo(x + width, y + cut);
  graphics.lineTo(x + width, y + height - cut);
  graphics.lineTo(x + width - cut, y + height);
  graphics.lineTo(x + cut, y + height);
  graphics.lineTo(x, y + height - cut);
  graphics.lineTo(x, y + cut);
  graphics.closePath();
  graphics.fillPath();
  graphics.strokePath();
}
